import { useState, useCallback, useEffect } from "react";

export function useColumnSettings(tableId) {
    const storageKey = `table-columns-${tableId}`;

    // Load saved settings from localStorage
    const loadSavedSettings = useCallback(() => {
        try {
            const saved = localStorage.getItem(`table-columns-${tableId}`);
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    }, [tableId]);

    const [sortConfig, setSortConfig] = useState({ column: null, direction: null });
    const [hiddenColumns, setHiddenColumns] = useState(() => new Set());
    const [columnOrder, setColumnOrder] = useState([]);
    const [columnWidths, setColumnWidths] = useState({});

    // Reset state when tableId changes
    useEffect(() => {
        const saved = loadSavedSettings();
        setSortConfig({ column: null, direction: null });
        setHiddenColumns(new Set(saved.hidden || []));
        setColumnOrder(saved.order || []);
        setColumnWidths(saved.widths || {});
    }, [tableId, loadSavedSettings]);

    // Save to localStorage whenever settings change
    const saveToStorage = useCallback((updates) => {
        try {
            const current = loadSavedSettings();
            const next = { ...current, ...updates };
            localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {}
    }, [storageKey]);

    const toggleSort = useCallback((columnName) => {
        setSortConfig((prev) => {
            if (prev.column !== columnName) return { column: columnName, direction: "asc" };
            if (prev.direction === "asc") return { column: columnName, direction: "desc" };
            return { column: null, direction: null };
        });
    }, []);

    const hideColumn = useCallback((columnName) => {
        setHiddenColumns((prev) => {
            const next = new Set([...prev, columnName]);
            saveToStorage({ hidden: [...next] });
            return next;
        });
    }, [saveToStorage]);

    const showColumn = useCallback((columnName) => {
        setHiddenColumns((prev) => {
            const next = new Set(prev);
            next.delete(columnName);
            saveToStorage({ hidden: [...next] });
            return next;
        });
    }, [saveToStorage]);

    const showAllColumns = useCallback(() => {
        setHiddenColumns(new Set());
        saveToStorage({ hidden: [] });
    }, [saveToStorage]);

    const setColumnWidth = useCallback((columnName, width) => {
        setColumnWidths((prev) => {
            const next = { ...prev, [columnName]: width };
            saveToStorage({ widths: next });
            return next;
        });
    }, [saveToStorage]);

    const reorderColumns = useCallback((newOrder) => {
        setColumnOrder(newOrder);
        saveToStorage({ order: newOrder });
    }, [saveToStorage]);

    const moveColumn = useCallback((fromIndex, toIndex) => {
        setColumnOrder((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            saveToStorage({ order: next });
            return next;
        });
    }, [saveToStorage]);

    return {
        sortConfig, toggleSort,
        hiddenColumns, hideColumn, showColumn, showAllColumns,
        columnWidths, setColumnWidth,
        columnOrder, reorderColumns, moveColumn,
    };
}
